import { stats_v2, sn_url } from '../utils/js/endpoints'
import {
    ajaxRequest,
    ajaxReceived,
    wsConnect,
    wsDisconnect,
    nhlVideoReceived,
    tickerGamesReceived,
} from './actions'
import store from '../store'

/*
 * APP AJAX/WS Action Creator
 */

let timer = null
let requests = []
let shared_gamecode
let shared_league
let aborting__connectionDispatcher = false //flag for halting any overlapping xhr aborts during rapid game selection or slow connection
let prev_status__connectionDispatcher = null

export const resetConnectionDispatcher = () => {
    clearTimeout(timer)
}

export const connectionDispatcher = (league, gamecode, isError = false, hasDelay = false) => {
    if (timer !== null) {
        clearTimeout(timer)
    }

    shared_gamecode = gamecode
    shared_league = league

    return dispatch => {
        const xhr = new XMLHttpRequest()
        let status
        let data
        let poll_wait = 5000 // 5 seconds
        let endpoint = `${stats_v2}/livetracker?league=${league}&id=${gamecode}`
        let games_endpoint_leagues = ['whc', 'wjc']

        if (games_endpoint_leagues.indexOf(league) !== -1) {
            const stats_league_naming = league === 'wjc' ? 'wjhc' : league // this is put here to obey APPs usage of the world juniors acronym
            endpoint = `${stats_v2}/games?league=${stats_league_naming}&id=${gamecode}`
        }

        dispatch(ajaxRequest)
        requests.push(xhr)

        // abort all requests except for the latest (used incase of potential confusion caused by rapid user game selection)
        if (!aborting__connectionDispatcher) {
            for (var i = 0; i < requests.length - 1; i++) {
                aborting__connectionDispatcher = true
                requests[i].abort()

                if (i === requests.length - 1) {
                    aborting__connectionDispatcher = false
                }
            }
        }

        xhr.open('GET', endpoint)
        xhr.onreadystatechange = function() {
            if (xhr.readyState === 4) {
                status = xhr.status
                data = JSON.parse(xhr.responseText)

                if (status === 200 && data.data.game) {
                    // pass the window location to help make sure we don't get a route mismatch
                    let window_location = window.location.pathname.split('/')[4]
                    let ajax_only_leagues = ['nba', 'whc', 'wjc', 'nhl', 'mlb']

                    // do initial load via ajax
                    if (
                        (prev_status__connectionDispatcher === 'In-Progress' &&
                            data.data.game.details.status === 'Final') ||
                        hasDelay
                    ) {
                        poll_wait = 15000 // increase to 15 seconds when status changes from In-Progress to Final
                    } else {
                        dispatch(ajaxReceived(data.data, league, gamecode, window_location)) // give the data to the app

                        if (poll_wait === 15000) {
                            poll_wait = 5000 // return to normal poll rate in all other scenarios
                        }
                    }

                    prev_status__connectionDispatcher = data.data.game.details.status

                    // activate intervals?
                    let gameTime = data.data.game.details.timestamp
                    let now = (Date.now() / 1000) | 0
                    let spread = 72000 // 20 minutes
                    let activateInterval = Math.abs(gameTime - now) < spread

                    // if 'In-Progress' use websockets else ajax
                    if (data.data.game.details.status === 'In-Progress' && !isError) {
                        if (ajax_only_leagues.indexOf(league) !== -1) {
                            timer = setTimeout(() => {
                                dispatch(connectionDispatcher(league, gamecode, false))
                            }, poll_wait)
                        } else {
                            dispatch(wsConnect(league, gamecode))
                        }
                    } else if (isError && activateInterval) {
                        timer = setTimeout(() => {
                            dispatch(connectionDispatcher(league, gamecode, true))
                        }, poll_wait)
                    } else if (activateInterval) {
                        timer = setTimeout(() => {
                            dispatch(connectionDispatcher(league, gamecode, false))
                        }, poll_wait)
                    }
                } else {
                    dispatch({ type: 'NOT_AVAILABLE', isFetching: false })
                }
            }
        }
        xhr.onerror = function() {
            dispatch({ type: 'AJAX_ERROR' })
            timer = setTimeout(() => {
                dispatch(connectionDispatcher(league, gamecode, false))
            }, poll_wait)
        }
        xhr.send()
    }
}

/*
 * VISIBILITY API Action Creator
 */

const check = () => {
    if (document.hidden) {
        store.dispatch(wsDisconnect())
    } else {
        store.dispatch(connectionDispatcher(shared_league, shared_gamecode))
    }
}

export const visibilityCheck = status => {
    if (status === 'In-Progress') {
        document.addEventListener('visibilitychange', check)
    } else {
        document.removeEventListener('visibilitychange', check)
    }
}

/*
 * AJAX VIDEO FEED Action Creator
 */

let video_timer = null
let video_error_timer = null
let video_requests = []
let aborting_getVideo = false //flag for halting any overlapping xhr aborts during rapid tab selection or slow connection

export const getVideo = (continueLoop = true, init_load = false, gamecode) => {
    if (video_timer !== null) {
        clearTimeout(video_timer)
    }

    if (video_error_timer !== null) {
        clearTimeout(video_error_timer)
    }

    if (!continueLoop) {
        clearTimeout(video_timer)
        clearTimeout(video_error_timer)
        return null
    }

    const xhr = new XMLHttpRequest()
    let status
    let data

    video_requests.push(xhr)

    // abort all requests except for the latest (used incase of potential confusion caused by rapid user game selection)
    if (!aborting_getVideo) {
        for (var i = 0; i < video_requests.length - 1; i++) {
            aborting_getVideo = true
            video_requests[i].abort()

            if (i === video_requests.length - 1) {
                aborting_getVideo = false
            }
        }
    }

    if (init_load) {
        store.dispatch({ type: 'VIDEO_INIT_FEED_REQUEST' })
    }

    xhr.open('GET', `${sn_url}/wp-json/snapi/v0.1/video-page/?game_id=${gamecode}`)
    xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
            status = xhr.status
            try {
                data = JSON.parse(xhr.responseText)
            } catch (e) {
                console.log(e)
            }

            if (status === 200 && data) {
                store.dispatch(nhlVideoReceived(data))
                store.dispatch({ type: 'VIDEO_FEED_REQUEST_COMPLETE' })
                video_timer = setTimeout(() => {
                    getVideo(true, false, gamecode)
                }, 5000)
            } else {
                store.dispatch({ type: 'VIDEO_FEED_ERROR' })
                video_timer = setTimeout(() => {
                    getVideo(true, false, gamecode)
                }, 5000)
            }
        }
    }
    xhr.onerror = function() {
        store.dispatch({ type: 'VIDEO_FEED_ERROR' })
        video_error_timer = setTimeout(() => {
            getVideo(true, true, gamecode)
        }, 5000)
    }
    xhr.send()
}

/*
 * AJAX Mobile Ticker Action Creator
 */

let ticker_timer = null
let ticker_error_timer = null
let ticker_requests = []
let aborting_getTickerGames = false //flag for halting any overlapping xhr aborts during rapid tab selection or slow connection

export const getTickerGames = (continueLoop = true, init_load = false, day, league) => {
    if (ticker_timer !== null) {
        clearTimeout(ticker_timer)
    }
    if (ticker_error_timer !== null) {
        clearTimeout(ticker_error_timer)
    }

    if (!continueLoop) {
        clearTimeout(ticker_timer)
        clearTimeout(ticker_error_timer)
        return null
    }

    if (init_load === true) {
        store.dispatch({ type: 'TICKER_INIT_FEED_REQUEST' })
    }

    const xhr = new XMLHttpRequest()
    let status
    let data

    ticker_requests.push(xhr)

    // abort all requests except for the latest (used incase of potential confusion caused by rapid user game selection)
    if (!aborting_getTickerGames) {
        for (var i = 0; i < ticker_requests.length - 1; i++) {
            aborting_getTickerGames = true
            ticker_requests[i].abort()

            if (i === ticker_requests.length - 1) {
                aborting_getTickerGames = false
            }
        }
    }

    xhr.open('GET', `${stats_v2}/ticker?league=${league}&day=${day}`)
    xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
            status = xhr.status
            try {
                data = JSON.parse(xhr.responseText)
            } catch (e) {
                console.log(e)
            }

            if (status === 200 && data) {
                let games = data.data.games || []
                if (games && games.constructor === Array && games.length > 0) {
                    let tickerGameOrder = {
                        'In-Progress': 1,
                        'Pre-Game': 2,
                        Final: 3,
                    } // game status order

                    games.sort((a, b) => {
                        const orderA = tickerGameOrder[a.game_status] || 4, // if delayed status, order last
                            orderB = tickerGameOrder[b.game_status] || 4 // if delayed status, order last
                        if (orderA === orderB) {
                            return a.timestamp - b.timestamp
                        } // secondary sort => timestamp
                        return orderA - orderB // primary sort => game status
                    })

                    // Move Toronto team to front
                    var torontoTeamInd = 0
                    games.forEach((g, i) => {
                        if ('Toronto' === g.home_team.city || 'Toronto' === g.visiting_team.city) {
                            torontoTeamInd = i
                        }
                    })
                    games.splice(0, 0, games.splice(torontoTeamInd, 1)[0])
                }

                store.dispatch(tickerGamesReceived(games))
                ticker_timer = setTimeout(() => {
                    getTickerGames(true, false, day, league)
                }, 5000) //TODO: Make sure date is the correct type of variable
            } else {
                store.dispatch({ type: 'TICKER_FEED_ERROR' })
                ticker_timer = setTimeout(() => {
                    getTickerGames(true, false, day, league)
                }, 5000) //TODO: Make sure date is the correct type of variable
            }
        }
    }
    xhr.onerror = function() {
        store.dispatch({ type: 'TICKER_FEED_ERROR' })
        ticker_error_timer = setTimeout(() => {
            getTickerGames(true, false, day, league)
        }, 5000)
    }
    xhr.send()
}

/*
 * AJAX SUMMARY VIDEO Action Creator
 */

let video_summary_requests = []
let aborting_getSummaryVideo = false //flag for halting any overlapping xhr aborts during rapid tab selection or slow connection

export const getSummaryVideo = (abortOnly = false, gamecode) => {
    if (!gamecode) {
        return null
    }

    if (abortOnly) {
        for (var x = 0; x < video_summary_requests.length; x++) {
            video_summary_requests[x].abort()
        }
        return null
    }

    const xhr = new XMLHttpRequest()
    let status
    let data

    video_summary_requests.push(xhr)

    // abort all requests except for the latest (used incase of potential confusion caused by rapid user game selection)
    if (!aborting_getSummaryVideo) {
        for (var i = 0; i < video_summary_requests.length - 1; i++) {
            aborting_getSummaryVideo = true
            video_summary_requests[i].abort()

            if (i === video_summary_requests.length - 1) {
                aborting_getSummaryVideo = false
            }
        }
    }

    xhr.open('GET', `https://www.sportsnet.ca/wp-json/snapi/v0.1/video-page/?game_id=${gamecode}`)
    xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
            status = xhr.status
            try {
                data = JSON.parse(xhr.responseText)
            } catch (e) {
                console.log(e)
            }

            if (status === 200 && data) {
                store.dispatch({ type: 'GET_SUMMARY_VIDEO', data: data })
            }
        }
    }
    xhr.onerror = function(e) {
        console.log(e)
    }
    xhr.send()
}
